import { noop } from 'lodash';
import { createContext, memo, type ReactNode, useContext, useMemo, useState } from 'react';

import { reportErrorToBugsnag } from '@air/utils-error';

import { isDevOrTestStage } from '~/swr-hooks/utils';

export type PDFContentControlsContextValue = {
  showOriginal: boolean;
  setShowOriginal: (showOriginal: boolean) => void;
};

const PDF_CONTENT_CONTROLS_CONTEXT_DEFAULT_VALUE: PDFContentControlsContextValue = {
  showOriginal: false,
  setShowOriginal: noop,
};

export const PDFContentControlsContext = createContext<PDFContentControlsContextValue>(
  PDF_CONTENT_CONTROLS_CONTEXT_DEFAULT_VALUE,
);

export type PDFContentControlsContextProviderProps = {
  children: ReactNode;
};

export const PDFContentControlsContextProvider = memo(({ children }: PDFContentControlsContextProviderProps) => {
  const [showOriginal, setShowOriginal] = useState(false);
  const value = useMemo(() => ({ showOriginal, setShowOriginal }), [showOriginal]);

  return <PDFContentControlsContext.Provider value={value}>{children}</PDFContentControlsContext.Provider>;
});

PDFContentControlsContextProvider.displayName = 'PDFContentControlsContextProvider';

export const usePDFContentControlsContext = (): PDFContentControlsContextValue => {
  const context = useContext(PDFContentControlsContext);

  if (context === PDF_CONTENT_CONTROLS_CONTEXT_DEFAULT_VALUE) {
    const error = new Error('usePDFContentControlsContext must be used within a PDFContentControlsContextProvider');
    if (isDevOrTestStage()) {
      throw error;
    } else {
      reportErrorToBugsnag({
        error,
        context: 'PDFContentControlsContext not found',
      });
    }
  }

  return context;
};
